// Rendert alle Canvas-Objekte und kapselt Drag-Handler.
// Drag committed Y.Map.set erst am Pointer-Up (Plan §S4.3 — keine Updates pro Frame).
// Resize laeuft denselben Pfad: lokales Preview waehrend Drag, Commit on Up.
// Block/Region sind nicht-interaktiv (Plan §S5.3 — Section-UI ist out-of-V1).
//
// Mode bestimmt das Klick-Verhalten:
//   "select" → Klick selektiert (+Shift = additive), Drag verschiebt.
//   "arrow"  → Klick ruft onArrowPick (fuer Connector-Erstellung).
//   "none"   → Pointer-Events laufen durch (Surface platziert dann ein Objekt).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type * as Y from "yjs";
import { newCanvasId } from "../../methods/insert-method.js";
import { autoRouteIntoFrame, autoRouteIntoRegion, regionAcceptsType } from "../auto-route.js";
import { cascadeResizeRegions, collectCascadeIds, filterUnlocked } from "../cascade.js";
import {
  applyMindmapLayout,
  computeDropTarget,
  levelOf,
  placeChild,
  placeSibling,
  wouldCreateCycle,
} from "../mindmap-layout.js";
import { type PortEdge, objectBounds } from "../object-bounds.js";
import { getObjectRenderer } from "../object-registry.js";
import { findFrameAt, findRegionAt } from "../region-routing.js";
import { type SmartGuide, findGuides, snapToGrid } from "../snap.js";
import { type EditableCommitIntent, EditableText } from "./EditableText.js";
import { type ResizeAnchor, ResizeHandles } from "./ResizeHandles.js";
import {
  type CanvasObject,
  type MindmapNodeObj,
  OBJECT_META,
  isCanvasObject,
  parseCanvasObject,
} from "./types.js";

const TAB_CLONE_GAP_PX = 16;

function defaultTextForClone(
  type: CanvasObject["type"],
  field: string,
  t: (key: string) => string,
): string {
  // Sticky / Text / Frame / Rect duerfen leer geklont werden (Schema erlaubt min(0)).
  if (type === "sticky" || type === "text") return "";
  if (type === "shape-rect" || type === "frame") return "";
  // Requirement / Pain / Dependency: Schema verlangt text.min(1) — Typ-Name als
  // Platzhalter, EditableText.select() markiert ihn beim Mount, User tippt drueber.
  if (field === "text") return t(`inspector.type.${type}`);
  return " ";
}

const PORT_EDGES: readonly PortEdge[] = ["n", "e", "s", "w"];

export type ObjectLayerMode = "select" | "arrow" | "none";

interface ObjectLayerProps {
  objects: readonly CanvasObject[];
  selection: ReadonlySet<string>;
  zoom: number;
  yMap: Y.Map<unknown>;
  onSelect: (id: string, additive: boolean) => void;
  onArrowPick?: (id: string) => void;
  mode: ObjectLayerMode;
  /** Falls im Arrow-Modus eine erste Source gewaehlt ist — hier markiert. */
  arrowFromId?: string | null;
  /** Edge-Drag-Connector: Pointer-Down auf einen Port startet eine Phantom-Linie. */
  onPortDown?: (fromId: string, edge: PortEdge, e: React.PointerEvent<HTMLDivElement>) => void;
  /** V2 Slice 4: Snap-to-Grid + Smart-Guides aktiv. */
  snapEnabled?: boolean;
  /** Inline-Edit-Status nach aussen melden — die Floating Property Bar versteckt sich,
   *  waehrend ein Element gerade direkt im Canvas editiert wird. */
  onEditingChange?: (id: string | null) => void;
}

interface MoveDragState {
  mode: "move";
  ids: string[];
  startScreenX: number;
  startScreenY: number;
  moved: boolean;
}

interface ResizeDragState {
  mode: "resize";
  id: string;
  anchor: ResizeAnchor;
  startScreenX: number;
  startScreenY: number;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  moved: boolean;
}

type DragState = MoveDragState | ResizeDragState;

interface ResizePreview {
  x: number;
  y: number;
  width: number;
  height: number;
}

// V2 Slice 1: Block/Region sind jetzt vollwertig interaktiv. Method-Scaffolds koennen
// vom User verschoben, skaliert und beschriftet werden; Cascade-Move/Resize bewegen
// Regions + parentRegionId-Children mit (siehe `cascade.ts`).
//
// V2-Registry: Resize-/Edit-Capability liest aus OBJECT_META — kein separates Set noetig.
const MIN_RESIZE_W = 80;
const MIN_RESIZE_H = 40;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

export function ObjectLayer({
  objects,
  selection,
  zoom,
  yMap,
  onSelect,
  onArrowPick,
  mode,
  arrowFromId,
  onPortDown,
  snapEnabled = false,
  onEditingChange,
}: ObjectLayerProps): JSX.Element {
  const { t } = useTranslation();
  const [dragPreview, setDragPreview] = useState<Record<string, { x: number; y: number }>>({});
  const [resizePreview, setResizePreview] = useState<{ id: string; box: ResizePreview } | null>(
    null,
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [smartGuides, setSmartGuides] = useState<SmartGuide[]>([]);
  const dragRef = useRef<DragState | null>(null);
  const editingRef = useRef<string | null>(null);

  useEffect(() => {
    editingRef.current = editingId;
    onEditingChange?.(editingId);
  }, [editingId, onEditingChange]);

  const startDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, id: string) => {
      if (mode !== "select") return;
      if (editingRef.current === id) return;
      // Locked-Objekte selektieren ja, aber nicht draggen.
      const raw = yMap.get(id);
      if (isCanvasObject(raw) && raw.locked) {
        e.stopPropagation();
        const additive = e.shiftKey;
        const wasSelected = selection.has(id);
        if (!wasSelected) onSelect(id, additive);
        return;
      }
      e.stopPropagation();
      const additive = e.shiftKey;
      const wasSelected = selection.has(id);
      if (!wasSelected) onSelect(id, additive);
      e.currentTarget.setPointerCapture(e.pointerId);
      const seedIds = additive || wasSelected ? Array.from(selection) : [id];
      if (!seedIds.includes(id)) seedIds.push(id);
      // Cascade-IDs gleich beim Drag-Start aufloesen, damit moveDrag-Preview die Children
      // mit-wandern zeigt und nicht pro Frame neu berechnet werden muss.
      const cascade = collectCascadeIds(yMap, seedIds);
      const dragIds = Array.from(filterUnlocked(yMap, cascade));
      dragRef.current = {
        mode: "move",
        ids: dragIds,
        startScreenX: e.clientX,
        startScreenY: e.clientY,
        moved: false,
      };
    },
    [mode, onSelect, selection, yMap],
  );

  const beginEdit = useCallback(
    (id: string): void => {
      const raw = yMap.get(id);
      if (!isCanvasObject(raw)) return;
      if (raw.locked) return;
      if (!OBJECT_META[raw.type].isEditable) return;
      setEditingId(id);
    },
    [yMap],
  );

  const onDoubleClickObj = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, id: string) => {
      if (mode !== "select") return;
      e.stopPropagation();
      if (!selection.has(id)) onSelect(id, false);
      beginEdit(id);
    },
    [mode, selection, onSelect, beginEdit],
  );

  const commitEdit = useCallback(
    (id: string, next: string): void => {
      const raw = yMap.get(id);
      setEditingId(null);
      if (!isCanvasObject(raw)) return;
      const field = OBJECT_META[raw.type].textField;
      if (!field) return;
      const patched = { ...raw, [field]: next };
      const parsed = parseCanvasObject(patched);
      if (!parsed) return;
      yMap.doc?.transact(() => yMap.set(id, parsed));
    },
    [yMap],
  );

  const cancelEdit = useCallback((): void => {
    setEditingId(null);
  }, []);

  // TAB im Inline-Edit: aktuellen Stand committen + Klon rechts daneben in Edit-Mode setzen.
  // Style (Color, Priority, Severity, Kind, Status, Locked, groupId) wird mit uebernommen.
  const cloneForTab = useCallback(
    (sourceId: string): void => {
      const raw = yMap.get(sourceId);
      if (!isCanvasObject(raw)) return;
      const meta = OBJECT_META[raw.type];
      const textField = meta.textField;
      if (!textField) return; // Block/Region/strukturell: kein TAB-Klon.
      const bounds = objectBounds(raw);
      const newId = newCanvasId();
      const clone = {
        ...(raw as Record<string, unknown>),
        id: newId,
        x: raw.x + bounds.w + TAB_CLONE_GAP_PX,
        y: raw.y,
        createdAt: Date.now(),
        [textField]: defaultTextForClone(raw.type, textField, t),
      };
      const parsed = parseCanvasObject(clone);
      if (!parsed) return;
      yMap.doc?.transact(() => yMap.set(newId, parsed));
      onSelect(newId, false);
      setEditingId(newId);
    },
    [yMap, onSelect, t],
  );

  // Mindmap-Intent-Klon: ENTER (Sibling) / TAB (Child) / Shift+TAB (Unindent).
  // Sibling/Child erzeugen neuen Node + setzen Edit-Mode. Unindent re-parented den
  // bestehenden Node, ohne neuen anzulegen.
  const cloneForIntent = useCallback(
    (sourceId: string, intent: EditableCommitIntent): void => {
      if (intent === "plain") return;
      const raw = yMap.get(sourceId);
      if (!isCanvasObject(raw) || raw.type !== "mindmap-node") return;
      const source = raw as MindmapNodeObj;

      const allById = new Map<string, MindmapNodeObj>();
      for (const v of yMap.values()) {
        if (isCanvasObject(v) && v.type === "mindmap-node") {
          allById.set(v.id, v as MindmapNodeObj);
        }
      }

      if (intent === "unindent") {
        if (!source.parentNodeId) return;
        const parent = allById.get(source.parentNodeId);
        const newParentId = parent?.parentNodeId ?? null;
        if (wouldCreateCycle(source.id, newParentId, allById)) return;
        const updated: MindmapNodeObj = { ...source, parentNodeId: newParentId };
        yMap.doc?.transact(() => {
          yMap.set(source.id, updated);
          // Auto-Layout fuer den neuen Subtree-Root des verschobenen Nodes.
          applyMindmapLayout(yMap, source.id);
        });
        return;
      }

      const newId = newCanvasId();
      const pos = intent === "child" ? placeChild(source, allById) : placeSibling(source, allById);
      const parentNodeId = intent === "child" ? source.id : source.parentNodeId;
      if (parentNodeId && wouldCreateCycle(newId, parentNodeId, allById)) return;
      const created: MindmapNodeObj = {
        id: newId,
        type: "mindmap-node",
        text: "",
        parentNodeId,
        x: pos.x,
        y: pos.y,
        width: source.width,
        height: source.height,
        createdBy: source.createdBy,
        createdAt: Date.now(),
        parentRegionId: null,
      };
      yMap.doc?.transact(() => {
        yMap.set(newId, created);
        // Auto-Layout fuer den Subtree-Root — Geschwister und ihre Subtrees verteilen
        // sich balanciert, kein manuelles Re-Layout noetig.
        applyMindmapLayout(yMap, source.id);
      });
      onSelect(newId, false);
      setEditingId(newId);
    },
    [yMap, onSelect],
  );

  // Enter/F2 starten Edit-Mode wenn genau ein editierbares Objekt selektiert ist.
  useEffect(() => {
    if (editingId !== null) return;
    if (selection.size !== 1) return;
    const [id] = selection;
    if (!id) return;
    const onKey = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.key !== "Enter" && e.key !== "F2") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const raw = yMap.get(id);
      if (!isCanvasObject(raw)) return;
      if (!OBJECT_META[raw.type].isEditable) return;
      e.preventDefault();
      setEditingId(id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection, editingId, yMap]);

  // Selection wechselt → Edit auf altem Objekt automatisch beenden.
  useEffect(() => {
    if (editingId !== null && !selection.has(editingId)) {
      setEditingId(null);
    }
  }, [selection, editingId]);

  const startResize = useCallback(
    (id: string, anchor: ResizeAnchor, e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      const handle = e.currentTarget;
      const parent = handle.parentElement as HTMLElement | null;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      const startW = rect.width / zoom;
      const startH = rect.height / zoom;
      const raw = yMap.get(id);
      if (!isCanvasObject(raw)) return;
      if (raw.locked) return;
      handle.setPointerCapture(e.pointerId);
      dragRef.current = {
        mode: "resize",
        id,
        anchor,
        startScreenX: e.clientX,
        startScreenY: e.clientY,
        startX: raw.x,
        startY: raw.y,
        startW,
        startH,
        moved: false,
      };
    },
    [yMap, zoom],
  );

  const computeResizeBox = useCallback(
    (drag: ResizeDragState, clientX: number, clientY: number): ResizePreview => {
      const dx = (clientX - drag.startScreenX) / zoom;
      const dy = (clientY - drag.startScreenY) / zoom;
      let w = drag.startW;
      let h = drag.startH;
      let x = drag.startX;
      let y = drag.startY;
      if (drag.anchor.includes("e")) w = drag.startW + dx;
      if (drag.anchor.includes("w")) {
        w = drag.startW - dx;
        x = drag.startX + dx;
      }
      if (drag.anchor.includes("s")) h = drag.startH + dy;
      if (drag.anchor.includes("n")) {
        h = drag.startH - dy;
        y = drag.startY + dy;
      }
      if (w < MIN_RESIZE_W) {
        if (drag.anchor.includes("w")) x = drag.startX + drag.startW - MIN_RESIZE_W;
        w = MIN_RESIZE_W;
      }
      if (h < MIN_RESIZE_H) {
        if (drag.anchor.includes("n")) y = drag.startY + drag.startH - MIN_RESIZE_H;
        h = MIN_RESIZE_H;
      }
      return { x, y, width: w, height: h };
    },
    [zoom],
  );

  const moveDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (drag.mode === "move") {
        let dx = (e.clientX - drag.startScreenX) / zoom;
        let dy = (e.clientY - drag.startScreenY) / zoom;
        if (Math.hypot(dx * zoom, dy * zoom) > 2) drag.moved = true;

        // V2 Slice 4: Snap-to-Grid + Smart-Guides anhand des Seed-Objekts.
        if (snapEnabled && drag.moved) {
          const seedId = drag.ids[0];
          const raw = seedId ? yMap.get(seedId) : null;
          if (isCanvasObject(raw)) {
            const b = objectBounds(raw);
            const dragBox = {
              x: raw.x + dx,
              y: raw.y + dy,
              width: b.w,
              height: b.h,
            };
            const dragIdSet = new Set(drag.ids);
            const others: CanvasObject[] = [];
            for (const value of yMap.values()) {
              if (!isCanvasObject(value)) continue;
              if (dragIdSet.has(value.id)) continue;
              others.push(value);
            }
            const guides = findGuides(dragBox, others);
            if (guides.length > 0) {
              for (const g of guides) {
                if (g.axis === "vertical") dx += g.snapDelta;
                else dy += g.snapDelta;
              }
            } else {
              const snappedX = snapToGrid(raw.x + dx);
              const snappedY = snapToGrid(raw.y + dy);
              dx = snappedX - raw.x;
              dy = snappedY - raw.y;
            }
            setSmartGuides(guides);
          }
        } else if (smartGuides.length > 0) {
          setSmartGuides([]);
        }

        const next: Record<string, { x: number; y: number }> = {};
        for (const id of drag.ids) next[id] = { x: dx, y: dy };
        setDragPreview(next);
      } else {
        const dxs = e.clientX - drag.startScreenX;
        const dys = e.clientY - drag.startScreenY;
        if (Math.hypot(dxs, dys) > 2) drag.moved = true;
        setResizePreview({ id: drag.id, box: computeResizeBox(drag, e.clientX, e.clientY) });
      }
    },
    [zoom, computeResizeBox, snapEnabled, smartGuides, yMap],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer-Capture kann bereits releasiert sein — kein Fehler.
      }
      dragRef.current = null;
      if (drag.mode === "move") {
        if (!drag.moved) {
          setDragPreview({});
          return;
        }
        let dx = (e.clientX - drag.startScreenX) / zoom;
        let dy = (e.clientY - drag.startScreenY) / zoom;
        // V2 Slice 4: identische Snap-Korrektur wie im moveDrag-Preview anwenden.
        if (snapEnabled) {
          const seedId = drag.ids[0];
          const raw = seedId ? yMap.get(seedId) : null;
          if (isCanvasObject(raw)) {
            const b = objectBounds(raw);
            const dragBox = { x: raw.x + dx, y: raw.y + dy, width: b.w, height: b.h };
            const dragIdSet = new Set(drag.ids);
            const others: CanvasObject[] = [];
            for (const value of yMap.values()) {
              if (!isCanvasObject(value)) continue;
              if (dragIdSet.has(value.id)) continue;
              others.push(value);
            }
            const guides = findGuides(dragBox, others);
            if (guides.length > 0) {
              for (const g of guides) {
                if (g.axis === "vertical") dx += g.snapDelta;
                else dy += g.snapDelta;
              }
            } else {
              const snappedX = snapToGrid(raw.x + dx);
              const snappedY = snapToGrid(raw.y + dy);
              dx = snappedX - raw.x;
              dy = snappedY - raw.y;
            }
          }
        }
        // drag.ids enthaelt bereits Cascade + Lock-Filter (Aufloesung beim startDrag).
        // Eine Transaction → Undo macht 1 Schritt, Yjs pusht atomar.
        yMap.doc?.transact(() => {
          for (const id of drag.ids) {
            const raw = yMap.get(id);
            if (!isCanvasObject(raw)) continue;
            yMap.set(id, { ...raw, x: raw.x + dx, y: raw.y + dy });
          }
          // MoSCoW Auto-Route bei Single-Drag eines Requirements:
          //   - landet das Req in einer Region mit semanticHint.priority → priority
          //     uebernehmen, an freien Slot rasten, parentRegionId setzen.
          //   - verlaesst es den Block (war drin, ist jetzt frei) → parentRegionId
          //     zuruecksetzen, priority unveraendert.
          if (drag.ids.length === 1) {
            const movedId = drag.ids[0];
            if (movedId) {
              const moved = yMap.get(movedId);
              // Mindmap-Drag-Re-Parent: Drop auf einen anderen Mindmap-Node setzt diesen
              // als neuen Parent. Drop ohne Target = no-op (Auto-Layout setzt den Node
              // visuell zurueck in den Subtree).
              if (isCanvasObject(moved) && moved.type === "mindmap-node") {
                const mindmapById = new Map<string, MindmapNodeObj>();
                for (const v of yMap.values()) {
                  if (isCanvasObject(v) && v.type === "mindmap-node") {
                    mindmapById.set(v.id, v as MindmapNodeObj);
                  }
                }
                const bounds = objectBounds(moved);
                const centerX = moved.x + bounds.w / 2;
                const centerY = moved.y + bounds.h / 2;
                const target = computeDropTarget(moved.id, centerX, centerY, mindmapById);
                if (target && target !== (moved as MindmapNodeObj).parentNodeId) {
                  yMap.set(moved.id, {
                    ...(moved as MindmapNodeObj),
                    parentNodeId: target,
                  });
                  applyMindmapLayout(yMap, target);
                } else {
                  // Kein neuer Parent — Layout setzt Node zurueck in seinen Subtree.
                  applyMindmapLayout(yMap, moved.id);
                }
              } else if (isCanvasObject(moved)) {
                const b = objectBounds(moved);
                const cx = moved.x + b.w / 2;
                const cy = moved.y + b.h / 2;
                const region = findRegionAt(yMap, cx, cy);
                if (region && regionAcceptsType(region, moved.type)) {
                  const result = autoRouteIntoRegion(yMap, moved, region);
                  if (result) {
                    const patched = {
                      ...moved,
                      x: result.x,
                      y: result.y,
                      parentRegionId: region.id,
                      parentFrameId: undefined,
                      ...result.hintPatch,
                    };
                    const parsed = parseCanvasObject(patched);
                    if (parsed) yMap.set(moved.id, parsed);
                  }
                } else {
                  const frame = findFrameAt(yMap, cx, cy);
                  if (frame && frame.id !== moved.id) {
                    const pos = autoRouteIntoFrame(frame, moved);
                    const patched = {
                      ...moved,
                      x: pos.x,
                      y: pos.y,
                      parentRegionId: null,
                      parentFrameId: frame.id,
                    };
                    const parsed = parseCanvasObject(patched);
                    if (parsed) yMap.set(moved.id, parsed);
                  } else if (moved.parentRegionId !== null || moved.parentFrameId !== undefined) {
                    const patched = { ...moved, parentRegionId: null, parentFrameId: undefined };
                    const parsed = parseCanvasObject(patched);
                    if (parsed) yMap.set(moved.id, parsed);
                  }
                }
              }
            }
          }
        });
        setDragPreview({});
        setSmartGuides([]);
      } else {
        if (!drag.moved) {
          setResizePreview(null);
          return;
        }
        const box = computeResizeBox(drag, e.clientX, e.clientY);
        const raw = yMap.get(drag.id);
        if (!isCanvasObject(raw)) {
          setResizePreview(null);
          return;
        }
        // Block-Resize: Regions proportional skalieren + ihre Children mit-bewegen.
        const isBlock = raw.type === "block";
        const oldBox = isBlock
          ? {
              x: raw.x,
              y: raw.y,
              width: raw.type === "block" ? raw.width : 0,
              height: raw.type === "block" ? raw.height : 0,
            }
          : null;
        yMap.doc?.transact(() => {
          yMap.set(drag.id, {
            ...raw,
            x: box.x,
            y: box.y,
            width: box.width,
            height: box.height,
          });
          if (isBlock && oldBox && oldBox.width > 0 && oldBox.height > 0) {
            cascadeResizeRegions(yMap, drag.id, oldBox, {
              x: box.x,
              y: box.y,
              width: box.width,
              height: box.height,
            });
          }
        });
        setResizePreview(null);
      }
    },
    [yMap, zoom, computeResizeBox, snapEnabled],
  );

  const onArrowClickObj = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, id: string) => {
      if (mode !== "arrow" || !onArrowPick) return;
      e.stopPropagation();
      onArrowPick(id);
    },
    [mode, onArrowPick],
  );

  // Level-Map fuer Mindmap-Nodes — wird zur Render-Zeit als data-level am Wrapper gesetzt,
  // CSS faerbt die Pill entsprechend Tiefe (Root = Orange, dann hellere Tints).
  const mindmapLevelById = useMemo(() => {
    const byId = new Map<string, MindmapNodeObj>();
    for (const o of objects) {
      if (o.type === "mindmap-node") byId.set(o.id, o as MindmapNodeObj);
    }
    const levels = new Map<string, number>();
    for (const n of byId.values()) {
      levels.set(n.id, levelOf(n.id, byId));
    }
    return levels;
  }, [objects]);

  // Render-Sort V2 Slice 3:
  //   1) zOrder ?? 0 — User-kontrollierte Ebene (Bring-to-Front / Send-to-Back)
  //   2) Type-Order — Block (0) → Region (1) → Rest (2), damit Container per Default hinten liegt
  //   3) createdAt — stabile Sortierung bei Gleichstand
  const ordered = [...objects].sort((a, b) => {
    const za = a.zOrder ?? 0;
    const zb = b.zOrder ?? 0;
    if (za !== zb) return za - zb;
    const ta = order(a.type);
    const tb = order(b.type);
    if (ta !== tb) return ta - tb;
    return a.createdAt - b.createdAt;
  });

  return (
    <>
      {smartGuides.map((g, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: Smart-Guides leben nur waehrend Drag
          key={`guide-${i}`}
          className={`canvas-smart-guide canvas-smart-guide-${g.axis}`}
          style={
            g.axis === "vertical"
              ? {
                  left: g.position,
                  top: g.from,
                  height: g.to - g.from,
                }
              : {
                  top: g.position,
                  left: g.from,
                  width: g.to - g.from,
                }
          }
        />
      ))}
      {ordered.map((obj) => {
        const movePreview = dragPreview[obj.id];
        const rp = resizePreview && resizePreview.id === obj.id ? resizePreview.box : null;
        const x = rp ? rp.x : obj.x + (movePreview?.x ?? 0);
        const y = rp ? rp.y : obj.y + (movePreview?.y ?? 0);
        const selected = selection.has(obj.id);
        const isArrowSource = arrowFromId === obj.id;
        const resizeable = OBJECT_META[obj.type].isResizeable;
        // Block/Region sind in V2 voll interaktiv (Slice 1). Edge-Drag-Connector-Ports
        // bleiben aber out fuer Strukturobjekte, weil Links auf Method-Container semantisch
        // wenig Sinn machen.
        const isStructural = obj.type === "block" || obj.type === "region";
        let handlers: Partial<{
          onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
          onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
          onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
          onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
          onDoubleClick: (e: React.MouseEvent<HTMLDivElement>) => void;
        }> = {};
        if (mode === "select") {
          handlers = {
            onPointerDown: (e) => startDrag(e, obj.id),
            onPointerMove: moveDrag,
            onPointerUp: endDrag,
            onDoubleClick: (e) => onDoubleClickObj(e, obj.id),
          };
        } else if (mode === "arrow" && !isStructural) {
          handlers = {
            onClick: (e) => onArrowClickObj(e, obj.id),
          };
        }
        // mode === "none" → keine Handler, Clicks gehen an die Surface
        const editing = editingId === obj.id;
        const showPorts = mode === "select" && !isStructural && !editing && !!onPortDown;
        const mindmapLevel = obj.type === "mindmap-node" ? mindmapLevelById.get(obj.id) : undefined;
        return (
          <ObjectView
            key={obj.id}
            obj={obj}
            x={x}
            y={y}
            {...(mindmapLevel !== undefined ? { mindmapLevel } : {})}
            selected={selected}
            isArrowSource={isArrowSource}
            handlers={handlers}
            resizePreview={rp}
            showResizeHandles={selected && resizeable && mode === "select" && !editing}
            onStartResize={(anchor, e) => startResize(obj.id, anchor, e)}
            onResizeMove={moveDrag}
            onResizeEnd={endDrag}
            editing={editing}
            onCommitEdit={(next) => commitEdit(obj.id, next)}
            onCancelEdit={cancelEdit}
            onTabClone={() => cloneForTab(obj.id)}
            {...(obj.type === "mindmap-node"
              ? {
                  onCommitWithIntent: (intent: EditableCommitIntent) =>
                    cloneForIntent(obj.id, intent),
                }
              : {})}
            showPorts={showPorts}
            onPortDown={(edge, e) => onPortDown?.(obj.id, edge, e)}
          />
        );
      })}
    </>
  );
}

function order(type: CanvasObject["type"]): number {
  if (type === "block") return 0;
  if (type === "region") return 1;
  return 2;
}

interface ObjectViewProps {
  obj: CanvasObject;
  x: number;
  y: number;
  selected: boolean;
  isArrowSource: boolean;
  handlers: Partial<{
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  }>;
  resizePreview: ResizePreview | null;
  showResizeHandles: boolean;
  onStartResize: (anchor: ResizeAnchor, e: React.PointerEvent<HTMLDivElement>) => void;
  onResizeMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onResizeEnd: (e: React.PointerEvent<HTMLDivElement>) => void;
  editing: boolean;
  onCommitEdit: (next: string) => void;
  onCancelEdit: () => void;
  onTabClone: () => void;
  onCommitWithIntent?: (intent: EditableCommitIntent) => void;
  showPorts: boolean;
  onPortDown: (edge: PortEdge, e: React.PointerEvent<HTMLDivElement>) => void;
  /** Mindmap-spezifisch: Tiefe im Tree (0 = Root). Wird als data-level am Wrapper gesetzt. */
  mindmapLevel?: number;
}

function ObjectView({
  obj,
  x,
  y,
  selected,
  isArrowSource,
  handlers,
  resizePreview,
  showResizeHandles,
  onStartResize,
  onResizeMove,
  onResizeEnd,
  editing,
  onCommitEdit,
  onCancelEdit,
  onTabClone,
  onCommitWithIntent,
  showPorts,
  onPortDown,
  mindmapLevel,
}: ObjectViewProps): JSX.Element {
  const meta = OBJECT_META[obj.type];
  const className = `canvas-obj canvas-obj-${obj.type} ${selected ? "is-selected" : ""} ${
    isArrowSource ? "is-arrow-source" : ""
  }`;
  const sizeStyle: React.CSSProperties = { left: x, top: y };
  if (resizePreview) {
    sizeStyle.width = resizePreview.width;
    sizeStyle.height = resizePreview.height;
    sizeStyle.maxWidth = "none";
  } else if (meta.hasExplicitSize) {
    const sized = obj as CanvasObject & { width: number; height: number };
    sizeStyle.width = sized.width;
    sizeStyle.height = sized.height;
  } else {
    const optional = obj as CanvasObject & { width?: number; height?: number };
    if (typeof optional.width === "number") {
      sizeStyle.width = optional.width;
      // Explizite User-Resize muss CSS-max-width ueberschreiben, sonst clampt der Browser.
      sizeStyle.maxWidth = "none";
    }
    if (typeof optional.height === "number") sizeStyle.height = optional.height;
  }

  const spec = getObjectRenderer(obj);
  const extraAttrs = spec.outerAttrs?.(obj) ?? {};
  const extraStyle = spec.outerStyle?.(obj) ?? {};

  const resizeHandles = showResizeHandles ? (
    <ResizeHandles onAnchorDown={onStartResize} onMove={onResizeMove} onEnd={onResizeEnd} />
  ) : null;

  const linkPorts = showPorts ? (
    <>
      {PORT_EDGES.map((edge) => (
        <div
          key={edge}
          className={`canvas-link-port canvas-link-port-${edge}`}
          data-port={edge}
          onPointerDown={(e) => onPortDown(edge, e)}
        />
      ))}
    </>
  ) : null;

  const editor = (initial: string): JSX.Element => (
    <EditableText
      initialValue={initial}
      onCommit={onCommitEdit}
      onCancel={onCancelEdit}
      {...(onCommitWithIntent ? { onCommitWithIntent } : { onTabClone })}
    />
  );

  return (
    <div
      {...handlers}
      {...extraAttrs}
      {...(mindmapLevel !== undefined ? { "data-level": String(mindmapLevel) } : {})}
      data-created-by={obj.createdBy}
      data-id={obj.id}
      className={className}
      style={{ ...sizeStyle, ...extraStyle }}
    >
      {spec.renderInner({ obj, editing, editor })}
      {resizeHandles}
      {linkPorts}
    </div>
  );
}
